import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { useTenantStore } from '../stores/tenantStore'

interface AuthContextValue {
  user: User | null
  session: Session | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<string | null>
  signUp: (email: string, password: string) => Promise<string | null>
  signInWithOAuth: (provider: 'google' | 'linkedin_oidc') => Promise<string | null>
  resetPassword: (email: string) => Promise<string | null>
  updatePassword: (password: string) => Promise<string | null>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const { setTenant, clearTenant } = useTenantStore()

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        setSession(data.session)
        setUser(data.session.user)
        _hydrateTenant(data.session)
      }
      setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s)
      setUser(s?.user ?? null)
      if (s) {
        _hydrateTenant(s)
      } else {
        clearTenant()
      }
    })

    return () => subscription.unsubscribe()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function _hydrateTenant(s: Session) {
    const meta = s.user.app_metadata ?? {}
    const tenantId = (meta.tenant_id as string | undefined) ?? s.user.id
    const plan = (meta.plan as string | undefined) ?? 'trial'
    const tenantName = (s.user.user_metadata?.company_name as string | undefined) ?? null
    setTenant(tenantId, tenantName ?? '', plan as 'trial' | 'starter' | 'growth' | 'enterprise', {
      currency: 'EUR',
      fiscalYearStart: 1,
      serviceLines: ['SG', 'LIC', 'DEV', 'RIV', 'PS'],
      alertThresholds: {},
    })
  }

  async function signIn(email: string, password: string): Promise<string | null> {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) return _mapError(error.message)
    return null
  }

  async function signUp(email: string, password: string): Promise<string | null> {
    const { error } = await supabase.auth.signUp({ email, password })
    if (error) return _mapSignUpError(error.message)
    return null
  }

  async function signInWithOAuth(provider: 'google' | 'linkedin_oidc'): Promise<string | null> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: window.location.origin + '/' },
    })
    if (error) return 'Accesso con provider esterno non riuscito. Riprovare.'
    return null
  }

  async function resetPassword(email: string): Promise<string | null> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + '/update-password',
    })
    if (error) return 'Invio email non riuscito. Riprovare.'
    return null
  }

  async function updatePassword(password: string): Promise<string | null> {
    const { error } = await supabase.auth.updateUser({ password })
    if (error) return _mapError(error.message)
    return null
  }

  async function signOut() {
    await supabase.auth.signOut()
    clearTenant()
  }

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signInWithOAuth, resetPassword, updatePassword, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth deve essere usato dentro AuthProvider')
  return ctx
}

function _mapError(msg: string): string {
  if (msg.includes('Invalid login credentials')) return 'Email o password non corretti.'
  if (msg.includes('Email not confirmed')) return 'Email non confermata. Controllare la casella di posta.'
  if (msg.includes('Too many requests')) return 'Troppi tentativi. Riprovare tra qualche minuto.'
  return 'Accesso non riuscito. Riprovare.'
}

function _mapSignUpError(msg: string): string {
  if (msg.includes('already registered') || msg.includes('User already registered')) return 'Email già registrata. Accedere o usare "Password dimenticata?".'
  if (msg.includes('Password should be at least')) return 'La password deve contenere almeno 6 caratteri.'
  if (msg.includes('Unable to validate email')) return 'Indirizzo email non valido.'
  if (msg.includes('Too many requests')) return 'Troppi tentativi. Riprovare tra qualche minuto.'
  return 'Registrazione non riuscita. Riprovare.'
}
